import './styles/main.scss'
import * as routes from './assets/routes'
import * as c from './assets/constants'
import { Route, Routes } from 'react-router-dom'
import { PrivateRoute } from 'components/Routing/PrivateRoute'
import { Helmet, HelmetProvider } from 'react-helmet-async'
import { useAppDispatch } from './hooks/hooks'
import { loadSessionAsync } from './store/authslice'
import { loadSiteSettingsAsync } from './store/settingsSlice'
import { ToastContainer } from 'react-toastify'
import { lazy, Suspense } from 'react'
import { Spinner } from 'react-bootstrap'
import { Footer } from 'components/Layout/Partials/Footer'
import { Header } from 'components/Layout/Partials/Header'
import SkipToContent from 'components/Common/SkipToContent'
import TitleAnnouncer from 'components/Common/TitleAnnouncer'
import SessionExpiryDialog from 'components/Common/SessionExpiryDialog'
import { useSessionExpiry } from 'hooks/useSessionExpiry'
import { useTestError } from 'hooks/useTestError'

const MyPage = lazy(() => import('components/Pages/MyPage/MyPage'))
const Activity = lazy(() => import('components/Pages/Activity/Activity'))
const CreateActivity = lazy(() => import('components/Pages/Activity/CreateActivity'))
const PreviewActivity = lazy(() => import('components/Pages/Activity/PreviewActivity'))
const EditActivity = lazy(() => import('components/Pages/Activity/EditActivity'))
const EditOrganisation = lazy(() => import('components/Pages/Organisation/EditOrganisation'))
const Search = lazy(() => import('components/Pages/Search/Search'))
const Home = lazy(() => import('components/Pages/Home'))
const Layout = lazy(() => import('components/Layout/Layout'))
const WideLayout = lazy(() => import('components/Layout/WideLayout'))
const PageRenderer = lazy(() => import('components/Routing/PageRenderer'))
const ResetPassword = lazy(() => import('components/Pages/ResetPassword'))
const RequestAccount = lazy(() => import('components/Pages/RequestAccount'))

const App = () => {
  const dispatch = useAppDispatch()
  dispatch(loadSessionAsync())
  dispatch(loadSiteSettingsAsync())
  useSessionExpiry()
  useTestError()

  return (
    <HelmetProvider>
      <TitleAnnouncer />
      <SkipToContent />
      <Header />
      <SessionExpiryDialog />
      <Helmet titleTemplate={`%s | Kulturkatalogen`}></Helmet>
      <ToastContainer containerId={'pushToastContainer'} enableMultiContainer={false} limit={1} />
      <Suspense
        fallback={
          <div className="p-4 justify-content-center align-items-center d-flex vh-100">
            <Spinner animation="border" variant="primary" />
          </div>
        }
      >
        <Routes>
          <Route
            path={`${routes.EDIT_ORGANISATION}/:orgId`}
            element={
              <PrivateRoute>
                <Layout title="Redigera organisation">
                  <EditOrganisation />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path={`${routes.PREVIEW_ACTIVITY}`}
            element={
              <PrivateRoute>
                <Layout title="Förhandsgranska aktivitet">
                  <PreviewActivity />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path={`${routes.PREVIEW_ACTIVITY}/:id`}
            element={
              <PrivateRoute>
                <Layout title="Förhandsgranska aktivitet">
                  <PreviewActivity />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path={`${routes.EDIT_ACTIVITY}/:id`}
            element={
              <PrivateRoute>
                <Layout title="Redigera aktivitet">
                  <EditActivity title={'Redigera aktivitet'} />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path={routes.CREATE_ACTIVITY}
            element={
              <PrivateRoute>
                <Layout title="Skapa aktivitet">
                  <CreateActivity title={c.CREATE_ACTIVITY_PAGE_TITLE} />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path={routes.MY_PAGE}
            element={
              <PrivateRoute>
                <Layout title="Min sida">
                  <MyPage />
                </Layout>
              </PrivateRoute>
            }
          />
          <Route
            path={routes.SEARCH}
            element={
              <WideLayout title="Sök">
                <Search />
              </WideLayout>
            }
          />
          <Route
            path={routes.DYNAMIC_POST}
            element={
              <Layout title="Aktivitet">
                <Activity />
              </Layout>
            }
          />
          <Route
            path={routes.DYNAMIC_PAGE}
            element={
              <Layout>
                <PageRenderer />
              </Layout>
            }
          />
          <Route
            path={routes.HOME}
            element={
              <WideLayout title="Hem">
                <Home />
              </WideLayout>
            }
          />
          <Route
            path={routes.RESET_PASSWORD}
            element={
              <Layout title="Återstall lösenord">
                <ResetPassword />
              </Layout>
            }
          />
          <Route
            path={routes.REQUEST_ACCOUNT}
            element={
              <Layout title="Ansök om konto">
                <RequestAccount />
              </Layout>
            }
          />
        </Routes>
      </Suspense>
      <Footer />
    </HelmetProvider>
  )
}

export default App
